import { Controller } from 'stimulus'
import { InputMasking } from 'classes/input-masking'

/**
 * Manages javascript for the borrower address step
 *
 * @class BorrowerAddressController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'moveIn',
    'nextVisible',
    'property',
    'propertyId',
    'template'
  ]

  /**
   * @function controllers.BorrowerAddressController.initialize
   * @memberof controllers
   * @description triggers when the controller initializes
   *
   * @async
   *
   * @return {undefined} spawns property fields/enables the next step button, scrolls view
   */
  async initialize() {
    await this._retrieveProperties()
    this.propertyTargets.forEach(property =>
      property.addEventListener('toggleNext', this._toggleNextEvent.bind(this))
    )
    if (this.sufficientHistory) {
      this._toggleNext(true)
    } else if (!this._unusedFieldSetPresent() && this._allDatesPresent()) {
      this._addPropertyFields()
    }
    this.moveInTargets.forEach(input =>
      input.addEventListener('calculate', this._calculateMonthsAndSpawn.bind(this))
    )
  }

  // Private

  /**
   * @function controllers.BorrowerAddressController._calculateMonthsAndSpawn
   * @memberof controllers
   * @description calculates the current number of months of residence history collected
   * and spawns another field if necessary
   * @private
   *
   * @param {Event} event
   *
   * @return {undefined} manipulates the dom
   */
  _calculateMonthsAndSpawn(event) {
    if (!this._unusedFieldSetPresent()) {
      if (this._monthsDiff(this.descendingMoveInDates.slice(-1)[0], new Date) < 24) {
        this.sufficientHistory = false
        this._toggleNext(false)
        if (this._allDatesPresent()) {
          this._addPropertyFields()
        }
      } else {
        this.sufficientHistory = true
        this._toggleNext(true)
      }
    } else {
      if (this._monthsDiff(this.descendingMoveInDates.slice(-1)[0], new Date) < 24) {
        if (this._allDatesPresent()) {
          this.sufficientHistory = false
          this._toggleNext(false)
          this._addPropertyFields()
        } else {
          this.sufficientHistory = false
          this._toggleNext(false)
        }
      } else {
        if (this._allDatesPresent()) {
          this.sufficientHistory = true
          this._toggleNext(true)
        } else {
          this.sufficientHistory = true
          this._removeExtraPropertyFieldSet()
          this._toggleNext(true)
        }
      }
    }
  }

  /**
   * @function controllers.BorrowerAddressController._addPropertyFields
   * @memberof controllers
   * @description adds property fields to the dom, spawns new fields if no data provided
   * @private
   *
   * @param {Object} [propertyData] - Object containing property data
   *
   * @return {undefined} manipulates the dom
   */
  _addPropertyFields() {
    const template = this.templateTarget.content.cloneNode(true)
    const count = this.propertyTargets.length + 1

    const fieldContainer = template.querySelector('.multi-form__fields')
    const formContainer = document.querySelector('.multi-form__page')

    const addressField = template.querySelector('input[id^=property_address]')
    const addressLabel = template.querySelector('label[id^=property_address]')

    const cityField = template.querySelector('input[id^=property_city]')
    const cityLabel = template.querySelector('label[id^=property_city]')

    const moveInField = template.querySelector('input[id^=property_move_in_on]')
    const moveInLabel = template.querySelector('label[id^=property_move_in_on]')

    const stateField = template.querySelector('select[id^=property_state]')
    const stateLabel = template.querySelector('label[id^=property_state]')

    const zipField = template.querySelector('input[id^=property_zip]')
    const zipLabel = template.querySelector('label[id^=property_zip]')

    /* global Promise */
    Promise.all([ addressField, cityField, stateField, zipField ]).then(fields =>
      fields.forEach(field => {
        this._incrementFieldProperties(field, count)
      })
    )

    Promise.all([ addressLabel, cityLabel, moveInLabel, stateLabel, zipLabel ]).then(labels =>
      labels.forEach(label =>
        this._incrementLabelProperties(label, count)
      )
    )

    this._incrementFieldProperties(moveInField, count)

    fieldContainer.dataset.controller = 'autocomplete gm-address-autocomplete property-address'
    fieldContainer.dataset.index = count
    fieldContainer.addEventListener('toggleNext', this._toggleNextEvent.bind(this))
    moveInField.addEventListener('calculate', this._calculateMonthsAndSpawn.bind(this))

    formContainer.appendChild(template)
    InputMasking.applyAll()
    fieldContainer.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
    return true
  }

  /**
   * @function controllers.BorrowerAddressController._allDatesPresent
   * @memberof controllers
   * @description checks that all properties on the page have move-in dates of the correct length
   * @private
   *
   * @return {Boolean} true if all properties have dates of the correct length
   */
  _allDatesPresent() {
    const result = this.moveInTargets.every(input => (input.value.length === 7))
    return result
  }

  /**
   * @function controllers.BorrowerAddressController._formatMonthYearDate
   * @memberof controllers
   * @description formats a string containing month/year info into a JS Date object
   * @private
   *
   * @param {String} dateString
   *
   * @return {Date} JS Date object
   */
  _formatMonthYearDate(dateString) {
    const dateArray = dateString.split('/')
    const year = parseInt(dateArray[1])
    const month = (parseInt(dateArray[0]) - 1) // because javascript zero indexes months

    return new Date(year, month)
  }

  /**
   * @function controllers.BorrowerAddressController._incrementFieldProperties
   * @memberof controllers
   * @description modifies the HTML properties of an input field to increment the
   * suffixes correctly
   * @private
   *
   * @param {Field} fieldElement
   * @param {Number} count
   *
   * @return {undefined} manipulates the dom
   */
  _incrementFieldProperties(fieldElement, count) {
    fieldElement.name = fieldElement.name.concat('_', count)
    fieldElement.id = fieldElement.id.concat(count)
    fieldElement.dataset.index = count
  }

  /**
   * @function controllers.BorrowerAddressController._incrementLabelProperties
   * @memberof controllers
   * @description modifies the HTML properties of a label to increment the suffixes
   * correctly
   * @private
   *
   * @param {Label} labelElement
   * @param {Number} count
   *
   * @return {undefined} manipulates the dom
   */
  _incrementLabelProperties(labelElement, count) {
    let elementType
    if (labelElement.htmlFor.includes('move_in_on')) {
      elementType = 'move_in_on'
    } else {
      elementType = labelElement.htmlFor.split('_')[1]
    }

    labelElement.htmlFor = labelElement.htmlFor.concat('_', count)
    labelElement.id = 'property_' + elementType + '_label_' + count
  }

  /**
   * @function controllers.BorrowerAddressController._monthsDiff
   * @memberof controllers
   * @description returns the number of months between two dates
   * @private
   *
   * @param {Date} startDate
   * @param {Date} endDate
   *
   * @return {Number} number of months between dates
   */
  _monthsDiff(startDate, endDate) {
    return endDate.getMonth() - startDate.getMonth() +
      (12 * (endDate.getFullYear() - startDate.getFullYear()))
  }

  /**
   * @function controllers.BorrowerAddressController._removeExtraPropertyFieldSet
   * @memberof controllers
   * @description removes the last set of property fields if they are empty and unneeded
   * @private
   *
   * @return {undefined} manipulates the dom
   */
  _removeExtraPropertyFieldSet() {
    this._removePropertyFieldSet(this.mostRecentProperty)
  }

  /**
   * @function controllers.BorrowerAddressController._removePropertyFieldSet
   * @memberof controllers
   * @description removes a given property fieldset from the dom
   * @private
   *
   * @param {Element} propertyElement to remove from DOM
   *
   * @return {undefined} manipulates the dom
   */
  _removePropertyFieldSet(propertyElement) {
    propertyElement.remove()
  }

  /**
   * @function controllers.BorrowerAddressController._retrieveProperties
   * @memberof controllers
   * @description fetches existing properties for the borrower from the backend if they exist and puts them onscreen
   * @private
   *
   * @return {undefined} manipulates the dom
   */
  async _retrieveProperties() {
    const borrowerId = this.data.get('borrower')
    const result = await fetch(`/v1/borrower_addresses/${borrowerId}`, {
      method:      'GET',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors'
    })
    const response = await result.json()
    if (response.properties.length === 0) {
      this.sufficientHistory = false
      return
    } else if (response.sufficient_history === true) {
      this.sufficientHistory = true
      return
    } else {
      this.sufficientHistory = false
      return
    }
  }

  /**
   * @function Controllers.BorrowerAddressController._toggleNext
   * @memberof Controllers
   * @description Toggles the visibility of the next step button based on the value passed
   * @private
   *
   * @param {boolean} value
   *
   * @return {undefined} manipulates the dom
   */
  _toggleNext(value) {
    this.nextVisibleTarget.value = value
    this.nextVisibleTarget.dispatchEvent(new Event('change'))
  }

  /**
   * @function Controllers.BorrowerAddressController._toggleNextEvent
   * @memberof Controllers
   * @description Toggles the visibility of the next step button based on the value
   * passed to it by the event
   * @private
   *
   * @param {event} event
   *
   * @return {undefined} manipulates the dom
   */
  _toggleNextEvent(event) {
    this.nextVisibleTarget.value = event.detail[0]
    this.nextVisibleTarget.dispatchEvent(new Event('change'))
  }

  /**
   * @function Controllers.BorrowerAddressController._unusedFieldSetPresent
   * @memberof Controllers
   * @description iterates over all properties and checks for persistence
   * @private
   *
   * @return {Boolean} true if a non-persisted property is present
   */
  _unusedFieldSetPresent() {
    return !(this.propertyTargets.length === this.propertyIdTargets.length)
  }

  /**
   * @function Controllers.BorrowerAddressController.descendingMoveInDates
   * @memberof Controllers
   * @description Getter for move in dates in descending order
   * @private
   *
   * @return {Array} array of descending move in dates
   */
  get descendingMoveInDates() {
    return this.moveInTargets.map(moveInField =>
      this._formatMonthYearDate(moveInField.value)
    ).filter(date => date.getDate() === date.getDate()).sort((a, b) => b - a)
  }

  /**
   * @function Controllers.BorrowerAddressController.mostRecentProperty
   * @memberof Controllers
   * @description Getter for the most recently created property
   * @private
   *
   * @return {Element} property DOM element
   */
  get mostRecentProperty() {
    const mostRecent = this.propertyTargets.sort((a, b) => b.dataset.index - a.dataset.index)[0]

    return mostRecent
  }
}
